import { Component, HostListener, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { SessionService } from 'src/app/core/backend-adapter/session.service';
import { PublisherUpdatesService } from './service/publisher-updates.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IAccount } from 'src/app/acct-comps/accounts.interfaces';
import { NotifyService } from 'src/app/core/layouts/notifications/notify/notify.service';
import { DialogService } from 'primeng/dynamicdialog';

// ⬇️ import your updated model types
import { UpdateItem, UpdatesResponse, UpdateStatus } from './models/update.model';

@Component({
  selector: 'app-publisher-updates',
  templateUrl: 'publisher-updates.component.html',
  styleUrls: ['publisher-updates.component.scss']
})
export class PublisherUpdatesComponent implements OnInit, OnDestroy {
  private userLoginId!: number;
  private currentAccount!: number;
  private ngUnsubscribe$ = new Subject<void>();

  public isLoadingData = true;
  public updatesList: UpdateItem[] = [];

  public tableWidth!: string;
  public tableHeight!: string;

  // ⬇️ Filters aligned to UpdateItem fields
  public filters: {
    publisher: string;
    status: string;
    field: string;
    account_ident: string;
    store_code: string;
    text: string;
    record_type: string;
  } = {
    publisher: '',
    status: '',
    field: '',
    account_ident: '',
    store_code: '',
    text: '',
    record_type: ''
  };

  public statusOptions: { label: string; value: UpdateStatus | '' }[] = [
    { label: 'All', value: '' },
    { label: 'Success', value: 'success' },
    { label: 'Accepted', value: 'accepted' },
    { label: 'Failed', value: 'failed' }
  ];

  public typeOptions = [
    { label: 'All', value: '' },
    { label: 'Update', value: 'update' },
    { label: 'Create', value: 'create' }
  ];

  public publisherOptions: { label: string; value: string }[] = [];

  constructor(
    private router: Router,
    private updatesService: PublisherUpdatesService,
    private sessionService: SessionService,
    private notifyService: NotifyService,
    private dialogService: DialogService
  ) {}

  ngOnInit(): void {
    this.onResize({ target: window });
    this.getCurrentAccountDetails();
    this.getCurrentUserDetails();
    this.getAllUpdates();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  private getCurrentAccountDetails(): void {
    this.sessionService
      .getSelectedAccount$()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((account: IAccount) => {
        this.currentAccount = account?._id as unknown as number;
      });
  }

  private getCurrentUserDetails(): void {
    this.sessionService
      .getCurrentUser$()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((user) => {
        this.userLoginId = user?.login?._id as unknown as number;
      });
  }

  extractRecordType(id?: string): 'update' | 'create' | 'other' {
    const prefix = (id ?? '').split('#')[0]?.toLowerCase();
    if (prefix === 'update' || prefix === 'create') return prefix;
    return 'other';
  }

  // show values? (for updates, where old/new/submitted exist)
  hasValues(u: UpdateItem): boolean {
    return this.extractRecordType(u.id) === 'update'
        && (u.old_value !== undefined || u.new_value !== undefined || u.submitted_value !== undefined);
  }

  // ⬇️ Type-safe filtered list
  get filteredUpdates(): UpdateItem[] {
    const f = {
      publisher: (this.filters.publisher ?? '').toLowerCase().trim(),
      status:    (this.filters.status ?? '').toLowerCase().trim(),
      field:     (this.filters.field ?? '').toLowerCase().trim(),
      account:   (this.filters.account_ident ?? '').toLowerCase().trim(),
      store:     (this.filters.store_code ?? '').toLowerCase().trim(),
      text:      (this.filters.text ?? '').toLowerCase().trim(),
      type:      (this.filters.record_type ?? '').toLowerCase().trim()
    };
    const lc = (v: unknown) => String(v ?? '').toLowerCase();

    return this.updatesList.filter((u) => {
      if (f.type) {
        const t = this.extractRecordType(u.id);
        if (t !== f.type) return false;
      }
      if (f.status    && lc(u.status) !== f.status) return false;
      if (f.publisher && !lc(u.publisher).includes(f.publisher)) return false;
      if (f.field     && !lc(u.field).includes(f.field)) return false;
      if (f.account   && !lc(u.account_ident).includes(f.account)) return false;
      if (f.store     && !lc(u.store_code).includes(f.store)) return false;
      if (f.text) {
        const t = lc(u.text ?? '');
        if (!t.includes(f.text)) return false;
      }
      return true;
    });
  }

  private getAllUpdates(): void {
    this.isLoadingData = true;

    if (!this.currentAccount) {
      this.notifyService.info('Please select an account to view updates.');
      this.isLoadingData = false;
      return;
    }

    this.updatesService
      .loadAllUpdates(this.currentAccount, this.userLoginId)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe({
        next: (response: UpdatesResponse) => {
          this.updatesList = response?.updates ?? [];
          this.isLoadingData = false;

          if (this.updatesList.length) {
            this.notifyService.success('Updates loaded successfully.');
            const publishers = Array.from(new Set(this.updatesList.map(u => u.publisher).filter(Boolean)));
            this.publisherOptions = [{ label: 'All', value: '' }, ...publishers.map(p => ({ label: this.toTitleCase(p), value: p }))];
          } else {
            this.notifyService.info('Updates not found.');
          }
        },
        error: () => {
          this.notifyService.error('Error while fetching updates.');
          this.isLoadingData = false;
        }
      });
  }

  toTitleCase(str: string): string {
    return (str || '')
      .toLowerCase()
      .split(' ')
      .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
      .join(' ');
  }

  onRowSelect(event: { data: UpdateItem }): void {
    const update = event.data;
    console.log('Selected update:', update);
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    const width = Math.min(event.target.innerWidth, 1900);
    const height = event.target.innerHeight;
    this.tableWidth = width - 100 + 'px';
    this.tableHeight = height - 650 + 'px'; // adjust this buffer to fit your header/filters
  }
}
